/**
 * The MFA methods supported by the API.
 *
 * @type {Object}
 */
export const MFA_METHODS = {
    TOTP: 'TOTP',
};

/**
 * The `status` values returned by `POST /v2/oauth/authenticate` when MFA is involved.
 * `MFA_ENROLLED` -> the user has a method enrolled and must verify a code.
 * `MFA_REQUIRED` -> the user has no method enrolled and must set one up first.
 *
 * @type {Object}
 */
export const MFA_STATUS = {
    MFA_ENROLLED: 'MFA_ENROLLED',
    MFA_REQUIRED: 'MFA_REQUIRED',
};

/**
 * The `status` values that require the user to complete an MFA flow before signing in.
 *
 * @type {Array}
 */
export const MFA_REQUIRED_STATUSES = [MFA_STATUS.MFA_ENROLLED, MFA_STATUS.MFA_REQUIRED];

/**
 * The routes of the MFA screens.
 * `RECOVERY`       -> where a user who cannot produce a code finds their options.
 * `SETUP`          -> where a user without an enrolled method sets one up.
 * `VERIFY_IDENTITY` -> where an enrolled user enters the verification code.
 *
 * @type {Object}
 */
export const MFA_ROUTES = {
    RECOVERY: '/auth/mfa/recovery',
    SETUP: '/auth/mfa/setup',
    VERIFY_IDENTITY: '/auth/mfa/verify-identity',
};

/**
 * The MFA route the user is sent to for each authenticate `status`.
 *
 * @type {Object}
 */
export const MFA_ROUTE_BY_STATUS = {
    [MFA_STATUS.MFA_ENROLLED]: MFA_ROUTES.VERIFY_IDENTITY,
    [MFA_STATUS.MFA_REQUIRED]: MFA_ROUTES.SETUP,
};

/**
 * The keys of the login credentials that are needed to complete the
 * post sign in redirect once the MFA code has been verified.
 *
 * NOTE: the user's password is never part of this list.
 *
 * @type {Array}
 */
export const MFA_LOGIN_CONTEXT_KEYS = [
    'redirect',
    'redirectState',
    'reload',
    'rememberUsername',
    'username',
];

/**
 * The number of digits of a TOTP verification code.
 *
 * @type {number}
 */
export const MFA_CODE_LENGTH = 6;

/**
 * The copy of the MFA verify identity screen.
 *
 * @type {Object}
 */
export const MFA_VERIFY_IDENTITY = {
    BACK: 'Back',
    CANT_ACCESS_CODE: 'Can’t access your code?',
    CODE_INPUT_LABEL: 'Digit DIGIT_POSITION of MFA verification code',
    SUBTITLE: 'Enter the 6-digit code from your authenticator app.',
    TITLE: 'Verify Your Identity',
    VERIFY: 'Verify',
};

/**
 * The copy of the MFA recovery screen, the screen a user who cannot produce a
 * code lands on from the verify identity screen.
 *
 * NOTE: the email verification option is display only, `TOTP` is the only
 * method the API supports, so the option is rendered disabled behind the
 * `EMAIL_BADGE` and only the support route is actionable.
 *
 * @type {Object}
 */
export const MFA_RECOVERY = {
    BACK: 'Back',
    EMAIL_BADGE: 'Coming Soon',
    EMAIL_DESCRIPTION: 'Receive a one-time code to your registered email address.',
    EMAIL_TITLE: 'Switch to Email Verification',
    RETURN_TO_SIGN_IN: 'Return to Sign-in',
    SUBTITLE: 'If you’ve lost access to your authentication method, here are your options.',
    SUPPORT_CTA: 'Submit a Request',
    SUPPORT_DESCRIPTION: 'A technical support agent can reset your MFA. Allow 1 business day.',
    SUPPORT_LABEL: 'Contact Technical Support',
    SUPPORT_TITLE: 'Contact Your Organization Admin, or Technical Support to Reset MFA',
    TITLE: 'Can’t Access Your Code?',
};

/**
 * The route of the public technical support form the recovery screen sends the
 * user to, it is opened in its own tab so the MFA challenge is left untouched.
 *
 * @type {string}
 */
export const MFA_SUPPORT_ROUTE = '/auth/tech-support';

/**
 * The `errorCode` values, they are strings in the API responses, the verify API
 * answers a failed verification with.
 *
 * @type {Object}
 */
export const MFA_ERROR_CODES = {
    INVALID_CODE: '1062',
    INVALID_TOKEN: '1061',
    MAX_ATTEMPTS: '1075',
    SESSION_EXPIRED: '1065',
    TOKEN_REPLAYED: '1063',
    TOKEN_REVOKED: '1076',
};

/**
 * The error states of the verify identity screen.
 * `invalidCode`    -> the code was rejected, the user can try again.
 * `maxAttempts`    -> every attempt of the challenge was used up.
 * `networkError`   -> the request never reached the API, or the API failed, so
 *                     the typed code was never evaluated.
 * `sessionExpired` -> the MFA token is gone, expired, replayed or revoked.
 *
 * @type {Object}
 */
export const MFA_ERROR_TYPES = {
    INVALID_CODE: 'invalidCode',
    MAX_ATTEMPTS: 'maxAttempts',
    NETWORK_ERROR: 'networkError',
    SESSION_EXPIRED: 'sessionExpired',
};

/**
 * The error state of the screen for each `errorCode` of the verify API.
 * Any code that is not listed here is rendered with the message of the API
 * itself and leaves the screen in its default, retryable, state.
 *
 * @type {Object}
 */
export const MFA_ERROR_TYPE_BY_CODE = {
    [MFA_ERROR_CODES.INVALID_CODE]: MFA_ERROR_TYPES.INVALID_CODE,
    [MFA_ERROR_CODES.INVALID_TOKEN]: MFA_ERROR_TYPES.SESSION_EXPIRED,
    [MFA_ERROR_CODES.MAX_ATTEMPTS]: MFA_ERROR_TYPES.MAX_ATTEMPTS,
    [MFA_ERROR_CODES.SESSION_EXPIRED]: MFA_ERROR_TYPES.SESSION_EXPIRED,
    [MFA_ERROR_CODES.TOKEN_REPLAYED]: MFA_ERROR_TYPES.SESSION_EXPIRED,
    [MFA_ERROR_CODES.TOKEN_REVOKED]: MFA_ERROR_TYPES.SESSION_EXPIRED,
};

/**
 * The error states the user cannot recover from on the screen itself, they
 * have to start over from the login screen.
 *
 * @type {Array}
 */
export const MFA_TERMINAL_ERROR_TYPES = [
    MFA_ERROR_TYPES.MAX_ATTEMPTS,
    MFA_ERROR_TYPES.SESSION_EXPIRED,
];

/**
 * The message rendered when the code is rejected, the user can try again.
 *
 * @type {string}
 */
export const MFA_INVALID_CODE_ERROR = 'The code you entered is invalid. Please try again.';

/**
 * The message rendered when every attempt of the challenge was used up.
 *
 * @type {string}
 */
export const MFA_MAX_ATTEMPTS_ERROR = 'Too many failed attempts. Please sign in again.';

/**
 * The message rendered when the MFA session is no longer usable.
 *
 * @type {string}
 */
export const MFA_SESSION_EXPIRED_ERROR = 'Your session has expired. Please sign in again.';

/**
 * The message rendered when the verification request never reached the API, or
 * the API answered with a server error, i.e. the code was never evaluated.
 *
 * @type {string}
 */
export const MFA_NETWORK_ERROR =
    'We couldn’t reach the server. Please check your connection and try again.';

/**
 * The message rendered for a failure that carries no message of its own, so the
 * screen never falls back to a raw client error string.
 *
 * @type {string}
 */
export const MFA_GENERIC_ERROR =
    'Something went wrong and we couldn’t verify your code. Please try again.';

/**
 * The lowest HTTP status the screen handles the same way as a request that
 * never reached the API.
 *
 * @type {number}
 */
export const MFA_SERVER_ERROR_STATUS = 500;

/**
 * The error states the code inputs are not cleared for, the typed code was
 * never evaluated so the user can retry it as it is.
 *
 * @type {Array}
 */
export const MFA_CODE_PRESERVED_ERROR_TYPES = [MFA_ERROR_TYPES.NETWORK_ERROR];

/**
 * The counter rendered above the inputs after a rejected code. It is built out
 * of three parts so the attempts left are emphasised, as per the design, and
 * `ATTEMPTS_LEFT` is replaced with the `remainingAttempts` of the response.
 *
 * @type {Object}
 */
export const MFA_ATTEMPTS_REMAINING = {
    ATTEMPTS_PLURAL: 'ATTEMPTS_LEFT attempts',
    ATTEMPTS_SINGULAR: 'ATTEMPTS_LEFT attempt',
    PREFIX: 'You have ',
    SUFFIX: ' left to enter the correct code.',
};

/**
 * The copy of the CTA that takes the user back to the login screen once the
 * MFA challenge can no longer be completed.
 *
 * @type {string}
 */
export const MFA_BACK_TO_SIGN_IN = 'Back to Sign In';

/**
 * The id of the node holding the verification error, the code inputs point at
 * it so assistive technology reads the reason of the failure.
 *
 * @type {string}
 */
export const MFA_ERROR_MESSAGE_ID = 'mfa-verify-identity-error';

/**
 * The id of the node holding the attempts left in the challenge, the code inputs
 * point at it so assistive technology reads it along with the error.
 *
 * @type {string}
 */
export const MFA_ATTEMPTS_MESSAGE_ID = 'mfa-verify-identity-attempts';

/**
 * The duration, in milliseconds, of the animation the code inputs play when the
 * typed code is rejected.
 *
 * @type {number}
 */
export const MFA_INVALID_CODE_ANIMATION_DURATION = 300;
